import type { EmbeddedStudioDefaultProps } from "../../rules/types.js";
import {
  DEFAULT_TERRAIN,
  type EmbedConfig,
  languageSchema,
  terrainVariantSchema,
  themeSchema,
  unitLengthSchema,
  unitPaceSchema,
  unitSlopeSchema,
  unitTemperatureSchema,
} from "../../search/rules/params.js";
import { resolveEmbedDefaults } from "./snippet.js";

/**
 * StoredEmbedDefaults is an organization's stored embed defaults as a HOST hands them over.
 *
 * The value fields are plain optional strings rather than the contract's enums, and that
 * looseness is the whole point of the type: a host reads these from its own account store,
 * which may be older than this build, may have been written by a different client, or may
 * simply not carry a field yet. Narrowing at the boundary would mean refusing an entire
 * organization's saved configuration over one unrecognised terrain slug.
 * {@link normalizeDefaultProps} folds each one into its domain enum instead, falling back per
 * FIELD.
 *
 * The toggles and allowlists are required, because a host with no answer for one of them has
 * no answer for the whole object, and the absent object is already a case this module
 * handles, by returning the platform baseline whole.
 */
export interface StoredEmbedDefaults {
  preferred_language?: string | undefined;
  preferred_theme?: string | undefined;
  preferred_unit_length?: string | undefined;
  preferred_unit_pace?: string | undefined;
  preferred_unit_slope?: string | undefined;
  preferred_unit_temperature?: string | undefined;
  segments_color?: string | undefined;
  terrain?: string | undefined;
  terrain_variant?: string | undefined;
  with_collection_itineraries: boolean;
  with_collection_zones: boolean;
  with_directions_control: boolean;
  with_download_control: boolean;
  with_navigation_control: boolean;
  with_overlays: string[];
  with_overlays_control: boolean;
  with_preferences_control: boolean;
  with_profile_analysis: boolean;
  with_profile_characteristics: boolean;
  with_profile_elevation: boolean;
  with_profile_pace_control: boolean;
  with_streetview_control: boolean;
  with_terrains: string[];
}

/**
 * embedFallbackProps is the baseline used when an organization has no default_props set at
 * all, so the form still initializes to a sensible, fully-typed config. It holds, field for
 * field, the defaults a new organization is provisioned with, so the fallback and the value a
 * new organization carries agree:
 * navigation + preferences on, the elevation/characteristics profile sections on
 * with analysis off, both collection content toggles on, street view + directions
 * + download + overlays-control off, empty layer allowlists, the topo light base
 * map, and no segment color (each segment keeps its own). A module constant, never mutated.
 */
const embedFallbackProps: EmbeddedStudioDefaultProps = {
  preferred_language: "en",
  preferred_theme: "light",
  preferred_unit_length: "metric",
  preferred_unit_pace: "per-time",
  preferred_unit_slope: "degrees",
  preferred_unit_temperature: "celsius",
  segments_color: "",
  terrain: DEFAULT_TERRAIN,
  terrain_variant: "light",
  with_collection_itineraries: true,
  with_collection_zones: true,
  with_directions_control: false,
  with_download_control: false,
  with_navigation_control: true,
  with_overlays: [],
  with_overlays_control: false,
  with_preferences_control: true,
  with_profile_analysis: false,
  with_profile_characteristics: true,
  with_profile_elevation: true,
  with_profile_pace_control: true,
  with_streetview_control: false,
  with_terrains: [],
};

/**
 * normalizeDefaultProps lifts a host's loosely-typed stored defaults into a
 * fully-typed EmbeddedStudioDefaultProps. A host that reads an organization's stored defaults
 * loosely typed normalizes them here before folding the URL overrides through
 * resolveEmbedConfig.
 *
 * The preference enums are parsed through the enum schemas of the studio's search params
 * (`safeParse`, falling back to the platform default when the stored value is unknown or
 * absent), so a value narrows to its domain enum without a cast; the terrain is kept as-is (a
 * free slug, fallback to DEFAULT_TERRAIN) and the segment color falls back to none; the boolean
 * toggles and the allowlists pass through as stored.
 *
 * @param props The stored defaults, or undefined when the organization has no default_props.
 * @returns The typed defaults; with no stored defaults, the platform baseline whole.
 */
export const normalizeDefaultProps = (
  props: StoredEmbedDefaults | undefined,
): EmbeddedStudioDefaultProps => {
  if (!props) {
    return embedFallbackProps;
  }

  const language = languageSchema.safeParse(props.preferred_language);
  const theme = themeSchema.safeParse(props.preferred_theme);
  const unitLength = unitLengthSchema.safeParse(props.preferred_unit_length);
  const unitPace = unitPaceSchema.safeParse(props.preferred_unit_pace);
  const unitSlope = unitSlopeSchema.safeParse(props.preferred_unit_slope);
  const unitTemperature = unitTemperatureSchema.safeParse(props.preferred_unit_temperature);
  const terrainVariant = terrainVariantSchema.safeParse(props.terrain_variant);

  return {
    preferred_language: language.success ? language.data : embedFallbackProps.preferred_language,
    preferred_theme: theme.success ? theme.data : embedFallbackProps.preferred_theme,
    preferred_unit_length: unitLength.success
      ? unitLength.data
      : embedFallbackProps.preferred_unit_length,
    preferred_unit_pace: unitPace.success ? unitPace.data : embedFallbackProps.preferred_unit_pace,
    preferred_unit_slope: unitSlope.success
      ? unitSlope.data
      : embedFallbackProps.preferred_unit_slope,
    preferred_unit_temperature: unitTemperature.success
      ? unitTemperature.data
      : embedFallbackProps.preferred_unit_temperature,
    segments_color: props.segments_color ?? embedFallbackProps.segments_color,
    terrain: props.terrain || embedFallbackProps.terrain,
    terrain_variant: terrainVariant.success
      ? terrainVariant.data
      : embedFallbackProps.terrain_variant,
    with_collection_itineraries: props.with_collection_itineraries,
    with_collection_zones: props.with_collection_zones,
    with_directions_control: props.with_directions_control,
    with_download_control: props.with_download_control,
    with_navigation_control: props.with_navigation_control,
    with_overlays: props.with_overlays,
    with_overlays_control: props.with_overlays_control,
    with_preferences_control: props.with_preferences_control,
    with_profile_analysis: props.with_profile_analysis,
    with_profile_characteristics: props.with_profile_characteristics,
    with_profile_elevation: props.with_profile_elevation,
    with_profile_pace_control: props.with_profile_pace_control,
    with_streetview_control: props.with_streetview_control,
    with_terrains: props.with_terrains,
  };
};

/**
 * resolveOrgEmbedDefaults resolves a host's loosely-typed stored defaults
 * into the fully-resolved EmbedConfig the wizard form initializes to and the Reset
 * action returns to. It normalizes them into a typed EmbeddedStudioDefaultProps
 * (filling any unset field from the platform fallback, see {@link normalizeDefaultProps}),
 * then folds it through the shared resolveEmbedDefaults so the form baseline matches exactly
 * what the live embed route would resolve for this organization with no URL overrides. It is
 * the same baseline buildEmbedSearch diffs against, so a configured value left at the
 * organization's default emits no param.
 *
 * @param props The organization's stored defaults, or undefined when it stores none.
 */
export const resolveOrgEmbedDefaults = (props: StoredEmbedDefaults | undefined): EmbedConfig =>
  resolveEmbedDefaults(normalizeDefaultProps(props));
